"use client";

import { motion } from "motion/react";
import { Sparkles, Bot, ShieldCheck, Award } from "lucide-react";
import { useLanguage } from "@/components/marketing/LanguageContext";
import { getSection02Data } from "./data";

const primaryLogo = "/marketing/fare-logo-trimmed.svg";
const coursesHero = "/marketing/courses_hero.jpg";

const NAVY = "#0B1D3A";

const featureIconMap = {
  Bot: Bot,
  ShieldCheck: ShieldCheck,
  Award: Award,
  Sparkles: Sparkles,
};

export default function Section02Desktop() {
  const { language } = useLanguage();
  const data = getSection02Data(language);

  return (
    <section
      id="section-02-coming-soon"
      aria-label="FARE Coming Soon Section"
      className="w-full relative overflow-hidden font-['Outfit'] fare-noise-overlay pt-4 pb-8 lg:pt-6 lg:pb-10 flex flex-col justify-center shrink-0"
      style={{
        background:
          "linear-gradient(165deg, #FFFFFF 0%, #F8FAFD 30%, #F0F4FF 60%, #E6EEFF 100%)",
      }}
    >
      <motion.div
        animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.05, 1] }}
        transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[5%] right-[10%] w-[700px] h-[700px] bg-gradient-radial from-[#C5D9FF]/40 to-transparent rounded-full blur-[140px] pointer-events-none -z-0"
      />
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none -z-0"
        style={{
          backgroundImage: `linear-gradient(${NAVY} 1px, transparent 1px), linear-gradient(90deg, ${NAVY} 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <div className="w-full flex flex-col lg:flex-row items-center justify-between relative z-10 pl-6 sm:pl-10 lg:pl-14 xl:pl-20 pr-0">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="w-full lg:w-[48%] xl:w-[46%] flex flex-col shrink-0 pr-6 lg:pr-10 max-w-[660px]"
        >
            <h2 className="text-3xl sm:text-4xl xl:text-[3.2rem] font-black text-[#0B1D3A] tracking-tight leading-[1.12] mb-4">
              {data.headline}
            </h2>

            <p className="text-base sm:text-lg font-normal text-[#475569] leading-relaxed max-w-[620px]">
              {data.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40, scale: 0.96 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="w-full lg:w-[52%] xl:w-[54%] relative flex items-center justify-end mt-10 lg:mt-0"
          >
            <div className="relative w-full h-[510px] xl:h-[550px] rounded-tl-[180px] lg:rounded-tl-[240px] xl:rounded-tl-[280px] rounded-bl-[70px] lg:rounded-bl-[100px] rounded-tr-none rounded-br-none overflow-hidden shadow-2xl border-l-2 border-t-2 border-b-2 border-white/90">
              <motion.img
                animate={{ scale: [1, 1.04, 1] }}
                transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
                src={coursesHero}
                alt="FARE Real Estate Professionals Learning"
                className="w-full h-full object-cover object-[center_35%]"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0B1D3A]/25 via-transparent to-transparent pointer-events-none" />

              <motion.div
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: [0, -6, 0] }}
                transition={{
                  opacity: { delay: 1, duration: 0.6 },
                  y: { delay: 1, duration: 4, repeat: Infinity, ease: "easeInOut" },
                }}
                className="absolute top-4 right-4 xl:top-5 xl:right-6 bg-white/95 backdrop-blur-xl rounded-[12px] px-3.5 py-2 border border-white shadow-xl flex items-center gap-2.5 z-40"
              >
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C99A2E] to-[#E2C068] text-white flex items-center justify-center shrink-0 shadow-xs">
                  <Sparkles size={15} strokeWidth={2.4} className="text-white" />
                </div>
                <div>
                  <div className="text-[9px] font-extrabold tracking-[0.16em] uppercase text-[#64748B] leading-none mb-0.5">
                    {data.comingSoonCard.floatingBadge.tag}
                  </div>
                  <div className="text-xs font-extrabold text-[#0B1D3A] leading-tight">
                    {data.comingSoonCard.floatingBadge.title}
                  </div>
                </div>
              </motion.div>
            </div>

            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 16 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="absolute z-30 max-w-[350px] sm:max-w-[375px] xl:max-w-[385px] left-4 md:left-6 lg:left-4 xl:left-8 top-1/2 -translate-y-1/2 rounded-2xl border border-white/85 bg-white/90 p-5 sm:p-6 text-center shadow-[0_20px_50px_rgba(11,29,58,0.25)] backdrop-blur-xl overflow-hidden"
            >
              <motion.div
                aria-hidden="true"
                animate={{ rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                className="pointer-events-none absolute -left-1/2 -top-1/2 h-[200%] w-[200%] bg-[conic-gradient(from_0deg,transparent_0_60%,#C99A2E_80%,#E2C068_90%,transparent_100%)] opacity-20"
              />

              <div className="relative z-10 flex flex-col items-center">
                <motion.div
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                  className="w-full flex items-center justify-center mb-2"
                >
                  <img
                    src={primaryLogo}
                    alt="FARE Logo"
                    className="w-[240px] sm:w-[260px] max-w-[90%] h-auto object-contain filter drop-shadow-xs"
                  />
                </motion.div>
                <h3 className="text-2xl sm:text-[25px] font-black tracking-tight text-[#0B1D3A] mb-3">
                  {data.comingSoonCard.title}
                </h3>

                <div className="w-full pt-3 border-t border-slate-200/80 flex flex-col gap-2.5 text-left">
                  {data.comingSoonCard.features.map((feat, idx) => {
                    const IconComponent = featureIconMap[feat.iconName] || Bot;
                    return (
                      <div
                        key={idx}
                        className="text-xs sm:text-[13px] font-semibold text-[#0B1D3A] flex items-center gap-2.5 p-1 rounded-md hover:bg-white/80 transition-colors"
                      >
                        <div
                          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 text-white shadow-xs"
                          style={{ backgroundColor: feat.color }}
                        >
                          <IconComponent size={14} strokeWidth={2.4} className="text-white" />
                        </div>
                        <span className="truncate">{feat.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
    );
  }
